
import {
    CategoryModel,
    CategoryUserModel,
    CategoryUserRoleModel,
    TenantUserLocationModel,
    LocationModel,
    RoleModel,
    TenantInvitationRoleModel,
    TenantInvitationCategoryUserModel,
    TenantInvitationCategoryUserRoleModel,
    TenantModel,
    TenantUserModel,
} from '@/libs/Api';
import Page from '@/Page.vue';
import { Guid } from '@/libs/common/functions/guid';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { tenantsStore, tenantUsersStore } from '@/libs/tenants/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { rolesStore } from '@/libs/roles/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import ApiService from '@/libs/core/api-service';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { categoriesStore } from '@/libs/categories/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';

@Component({})
export default class InviteAccountDialog extends Page {
    dialog = false;
    valid = true;
    saving = false;
    showInvitationRoleRules = false;
    showToastExternalLocationError = false;
    showToastCategoryRoleError = false;
    email = '';
    userName = '';
    password = '';
    firstName = '';
    employeeId = '';
    departmentId: string | null = null;
    positionId: string | null = null;
    categoryId: string | null = null;
    locale: string | null = 'cs';
    lastName = '';
    phoneNumber = '';
    pin = '';
    useQuickLogin = false;
    isExternal = false;
    accessAllCategories = true;
    loginType = 'email';
    invitationRoles: TenantInvitationRoleModel[] = [];
    allowedCategories: CategoryUserModel[] = [];
    allowedLocations: TenantUserLocationModel[] = [];
    roleId: string | null = null;
    locationId: string | null = null;
    categoryRoleSelects: { [key: string]: string | null } = {};

    pinRules = [
        (v: any) => !!v || this.$t('validation.thisFieldIsRequired'),
        (v: string) => (v && v.length <= 4) || this.$t('validation.max4numbers'),
        (v: string) => (v && /^[0-9]+$/gi.test(v)) || this.$t('validation.onlyNumbers'),
    ];

    protected get employeeIdRules() {
        const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
        return [
            (v: string) =>
                !v ||
                (v && v.toString().length <= tenant.quickLoginPersonalNumberPositionCount!) ||
                this.$t('validation.maxNumbers', { value: tenant.quickLoginPersonalNumberPositionCount! }),
        ];
    }

    get rules() {
        return [
            (v: any) =>
                !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$i18n.t('validation.emailIsRequired'),
        ];
    }

    get userNameRules() {
        return [(v: any) => !v || /^[a-zA-Z0-9.]{6,}$/.test(v) || this.$i18n.t('validation.userNameIsRequried')];
    }

    get passwordRules() {
        return [(v: any) => !!v || this.$i18n.t('validation.thisFieldIsRequired')];
    }

    get requiredRules() {
        return [(v: any) => !!v || this.$i18n.t('validation.thisFieldIsRequired')];
    }

    get activeRoles(): string[] {
        return this.invitationRoles?.map((x) => x.roleId!) ?? [];
    }

    get isSingleTenant(): boolean {
        return authStore.useGetter(AuthGetter.GetIsSingleTenant) ?? false;
    }

    get useTpm(): boolean {
        return (window as any).UseTpm;
    }

    @Watch('dialog')
    dialogChanged(v: boolean) {
        if (!v) this.close();
    }

    @Watch('roleId')
    onRoleIdChange(newVal: string | null, oldVal: string | null) {
        if (newVal && newVal !== oldVal) {
            this.$nextTick(() => {
                this.addRoleClick();
            });
        }
    }

    @Watch('locationId')
    onLocationIdChange(newVal: string | null, oldVal: string | null) {
        if (newVal && newVal !== oldVal) {
            this.$nextTick(() => {
                this.addLocationClick();
            });
        }
    }

    @Watch('categoryId')
    onCategoryIdChange(newVal: string | null, oldVal: string | null) {
        if (newVal && newVal !== oldVal) {
            this.$nextTick(() => {
                this.addAllowedCategoryClick();
            });
        }
    }

    @Watch('categoryRoleSelects', { deep: true })
    onCategoryRoleSelectsChange(newVal: { [key: string]: string | null }) {
        this.$nextTick(() => {
            Object.keys(newVal).forEach((categoryUserId) => {
                const roleId = newVal[categoryUserId];
                if (roleId) {
                    this.addCategoryRoleClick(categoryUserId, roleId);
                }
            });
        });
    }

    @Ref() form!: any;
    @Prop() tenantId!: string;

    open() {
        this.invitationRoles = [];
        this.email = '';
        this.userName = '';
        this.password = '';
        this.firstName = '';
        this.lastName = '';
        this.phoneNumber = '';
        this.employeeId = '';
        this.pin = '';
        this.departmentId = null;
        this.positionId = null;
        this.categoryId = null;
        this.accessAllCategories = true;
        this.allowedCategories = [];
        this.allowedLocations = [];
        this.useQuickLogin = false;
        this.loginType = 'email';
        this.dialog = true;
        this.showInvitationRoleRules = false;
        this.showToastCategoryRoleError = false;
        this.isExternal = false;
        this.locationId = null;
        this.categoryRoleSelects = {};
    }

    close() {
        this.dialog = false;
    }

    save() {
        this.showInvitationRoleRules = false;
        this.showToastCategoryRoleError = false;
        if (this.form.validate() && this.invitationRoles.length > 0) {
            if (this.isExternal && (!this.allowedLocations || this.allowedLocations.length === 0)) {
                this.showToastExternalLocationError = true;
                return;
            }
            // Validace: Pokud kategorie má vlastní role, musí mít alespoň jednu roli přiřazenou
            if (!this.accessAllCategories) {
                const categoriesWithCustomRoles = this.allowedCategories.filter((c) => c.inheritedRoles === false);
                const invalidCategories = categoriesWithCustomRoles.filter(
                    (c) => !c.categoryUserRoles || c.categoryUserRoles.length === 0
                );
                if (invalidCategories.length > 0) {
                    this.showToastCategoryRoleError = true;
                    return;
                }
            }
            const InviteTenantUserModel: TenantUserModel = {
                id: '00000000-0000-0000-0000-000000000000',
                user: {
                    id: '00000000-0000-0000-0000-000000000000',
                    email: this.loginType == 'email' ? this.email : null,
                    userName: this.loginType == 'userName' ? this.userName : null,
                    firstName: this.loginType == 'userName' ? this.firstName : null,
                    lastName: this.loginType == 'userName' ? this.lastName : null,
                    phoneNumber: this.loginType == 'userName' ? this.phoneNumber : null,
                    language: this.locale,
                },
                userRoles: this.invitationRoles,
                pwRequest: this.loginType == 'userName' ? this.password : null,
                employeeId: this.loginType == 'userName' ? this.employeeId : null,
                accessAllCategories: this.accessAllCategories,
                categories: this.allowedCategories,
                isExternal: this.isExternal,
                locations: this.allowedLocations,
                departmentId: this.departmentId,
                positionId: this.positionId,
            };
            tenantUsersStore.dispatch(CrudAction.Create, { item: InviteTenantUserModel });
            this.saving = true;
            this.subscribe(tenantUsersStore, CrudReponse.Create)
                .then((e: TenantUserModel) => {
                    if (this.loginType == 'userName' && this.useQuickLogin && this.pin != '' && this.pin != null) {
                        ApiService.api
                            .setQuickLoginPin(e.id!, this.pin)
                            .then(() => {
                                this.close();
                                this.saving = false;
                                this.$emit('change');
                            })
                            .catch(() => {
                                this.close();
                                this.saving = false;
                                this.$emit('change');
                            });
                    } else {
                        this.close();
                        this.saving = false;
                        this.$emit('change');
                    }
                })
                .catch(() => {
                    this.close();
                    this.saving = false;
                    this.$emit('change');
                });
        } else {
            this.showInvitationRoleRules = true;
        }
    }

    addRoleClick() {
        if (!this.roleId) return;
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        const role = roles.firstOrDefault((x) => x.id == this.roleId);
        if (role) {
            const exists = this.invitationRoles.some((x) => x.roleId == role.id);
            if (!exists) {
                this.invitationRoles!.push({
                    id: Guid.EmptyGuid(),
                    roleId: role.id,
                    role: role,
                } as TenantInvitationRoleModel);
            }
        }
        this.roleId = null;
    }

    addAllowedCategoryClick() {
        if (!this.categoryId) return;
        const categories = (categoriesStore.useGetter(CrudGetter.Data) as CategoryModel[]) ?? [];
        const category = categories.firstOrDefault((x) => x.id == this.categoryId);
        if (category) {
            const exists = this.allowedCategories.some((x) => x.categoryId == this.categoryId);
            if (!exists) {
                const newCategoryUser: CategoryUserModel = {
                    id: Guid.EmptyGuid(),
                    categoryId: this.categoryId,
                    category: category,
                    inheritedRoles: true,
                    categoryUserRoles: [],
                } as CategoryUserModel;
                this.allowedCategories.push(newCategoryUser);
                this.$set(this.categoryRoleSelects, newCategoryUser.id!, null);
            }
        }
        this.categoryId = null;
    }

    deleteCategoryClick(categoryUser: CategoryUserModel) {
        const index = this.allowedCategories.findIndex((x) => x.id == categoryUser.id);
        if (index > -1) {
            this.allowedCategories.splice(index, 1);
            if (categoryUser.id) {
                this.$delete(this.categoryRoleSelects, categoryUser.id);
            }
        }
    }

    addLocationClick() {
        if (!this.locationId) return;
        const exists = (this.allowedLocations ?? []).some((x) => x.locationId == this.locationId);
        if (!exists) {
            this.allowedLocations.push({
                id: Guid.EmptyGuid(),
                locationId: this.locationId,
            } as TenantUserLocationModel);
        }
        this.locationId = null;
    }

    deleteLocationClick(locationItem: TenantUserLocationModel) {
        const index = this.allowedLocations.findIndex(
            (x) => (x.id ?? x.locationId) == (locationItem.id ?? locationItem.locationId)
        );
        if (index > -1) {
            this.allowedLocations.splice(index, 1);
        }
    }

    getLocationName(locationId?: string | null): string {
        if (!locationId) return '';
        const all: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const found: LocationModel | undefined = (all as any).firstOrDefault
            ? (all as any).firstOrDefault((x: LocationModel) => x.id == locationId)
            : all.find((x) => x.id == locationId);
        return found?.name ?? '';
    }

    getCategoryRoles(categoryUser: CategoryUserModel): CategoryUserRoleModel[] {
        return categoryUser.categoryUserRoles ?? [];
    }

    getRoleName(roleId?: string | null): string {
        if (!roleId) return '-';
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        if (!roles) return roleId;
        const role = (roles as any).firstOrDefault
            ? (roles as any).firstOrDefault((x: RoleModel) => x.id == roleId)
            : roles.find((x) => x.id == roleId);
        return role?.name ?? roleId;
    }

    deleteCategoryRole(categoryUser: CategoryUserModel, index: number) {
        if (!categoryUser.categoryUserRoles) return;
        categoryUser.categoryUserRoles.splice(index, 1);
    }

    addCategoryRoleClick(categoryUserId: string, roleId: string) {
        const categoryUser = this.allowedCategories.find((x) => x.id == categoryUserId);
        if (!categoryUser) return;

        if (!categoryUser.categoryUserRoles) {
            this.$set(categoryUser, 'categoryUserRoles', []);
        }

        const exists = categoryUser.categoryUserRoles!.some((x) => x.roleId == roleId);
        if (!exists) {
            const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
            const role = (roles as any).firstOrDefault
                ? (roles as any).firstOrDefault((x: RoleModel) => x.id == roleId)
                : roles.find((x) => x.id == roleId);

            if (role) {
                categoryUser.categoryUserRoles!.push({
                    id: Guid.EmptyGuid(),
                    roleId: roleId,
                    role: role,
                } as CategoryUserRoleModel);
            }
        }

        this.$set(this.categoryRoleSelects, categoryUserId, null);
    }
}
